import type { BaseModelAttributes } from './base';
import { I18nLabels } from './common';
import { Locale } from './locale';

export enum AssetType {
  CHARACTER = 'character',  // 角色
  PROP = 'prop',           // 道具/物品
  SCENE = 'scene'          // 场景
}

export const AssetTypeLabels: I18nLabels<AssetType> = {
  [AssetType.CHARACTER]: { [Locale.ZH_CN]: '角色', [Locale.EN_US]: 'Character' },
  [AssetType.PROP]: { [Locale.ZH_CN]: '道具', [Locale.EN_US]: 'Prop' },
  [AssetType.SCENE]: { [Locale.ZH_CN]: '场景', [Locale.EN_US]: 'Scene' },
};

/**
 * 图片反推提示词模式
 */
export enum ImageAnalyzeMode {
  DEFAULT = 'default',       // 默认：全面分析（主体、风格、构图、色彩、光影）
  CHARACTER = 'character',   // 角色模式：提取角色外貌特征，忽略背景/构图/环境
  PROP = 'prop',             // 道具模式：提取道具外观特征
  SCENE = 'scene'            // 场景模式：提取场景环境特征，忽略特定人物细节
}

/**
 * 主体生成状态
 */
export enum AssetGenerationStatus {
  IDLE = 'idle',           // 未生成/空闲
  PENDING = 'pending',     // 等待生成
  GENERATING = 'generating', // 生成中
  COMPLETED = 'completed', // 生成完成
  FAILED = 'failed'        // 生成失败
}

export const AssetGenerationStatusLabels: I18nLabels<AssetGenerationStatus> = {
  [AssetGenerationStatus.IDLE]: { [Locale.ZH_CN]: '未生成', [Locale.EN_US]: 'Idle' },
  [AssetGenerationStatus.PENDING]: { [Locale.ZH_CN]: '等待中', [Locale.EN_US]: 'Pending' },
  [AssetGenerationStatus.GENERATING]: { [Locale.ZH_CN]: '生成中', [Locale.EN_US]: 'Generating' },
  [AssetGenerationStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
  [AssetGenerationStatus.FAILED]: { [Locale.ZH_CN]: '失败', [Locale.EN_US]: 'Failed' },
};

/**
 * 主体生成状态颜色
 */
export const AssetGenerationStatusColors: Record<AssetGenerationStatus, string> = {
  [AssetGenerationStatus.IDLE]: '#6B7280',      // 灰色
  [AssetGenerationStatus.PENDING]: '#F59E0B',   // 黄色
  [AssetGenerationStatus.GENERATING]: '#3B82F6', // 蓝色
  [AssetGenerationStatus.COMPLETED]: '#10B981', // 绿色
  [AssetGenerationStatus.FAILED]: '#EF4444'     // 红色
};

/**
 * 角色类型（仅对 CHARACTER 类型有效）
 * 用于区分主角、配角等
 */
export enum CharacterRole {
  PROTAGONIST = 'protagonist',     // 主角
  HERO = 'hero',                   // 男主
  HEROINE = 'heroine',            // 女主
  SUPPORTING = 'supporting',       // 配角
  ANTAGONIST = 'antagonist',       // 反派
  MINOR = 'minor',                 // 龙套
  MODEL = 'model'                  // 模特
}

export const CharacterRoleLabels: I18nLabels<CharacterRole> = {
  [CharacterRole.PROTAGONIST]: { [Locale.ZH_CN]: '主角', [Locale.EN_US]: 'Protagonist' },
  [CharacterRole.HERO]: { [Locale.ZH_CN]: '男主', [Locale.EN_US]: 'Hero' },
  [CharacterRole.HEROINE]: { [Locale.ZH_CN]: '女主', [Locale.EN_US]: 'Heroine' },
  [CharacterRole.SUPPORTING]: { [Locale.ZH_CN]: '配角', [Locale.EN_US]: 'Supporting' },
  [CharacterRole.ANTAGONIST]: { [Locale.ZH_CN]: '反派', [Locale.EN_US]: 'Antagonist' },
  [CharacterRole.MINOR]: { [Locale.ZH_CN]: '龙套', [Locale.EN_US]: 'Minor' },
  [CharacterRole.MODEL]: { [Locale.ZH_CN]: '模特', [Locale.EN_US]: 'Model' },
};

/**
 * 角色类型对应的颜色（用于UI显示）
 */
export const CharacterRoleColors: Record<CharacterRole, string> = {
  [CharacterRole.PROTAGONIST]: '#FF5C00',  // 橙色 - primary
  [CharacterRole.HERO]: '#10B981',         // 绿色
  [CharacterRole.HEROINE]: '#3B82F6',      // 蓝色
  [CharacterRole.SUPPORTING]: '#6B7280',   // 灰色
  [CharacterRole.ANTAGONIST]: '#EF4444',   // 红色
  [CharacterRole.MINOR]: '#9CA3AF',        // 浅灰色
  [CharacterRole.MODEL]: '#8B5CF6'         // 紫色
};

/**
 * 性别枚举（角色专用）
 */
export enum CharacterGender {
  MALE = 'male',       // 男性
  FEMALE = 'female',   // 女性
  OTHER = 'other'      // 其他
}

export const CharacterGenderLabels: I18nLabels<CharacterGender> = {
  [CharacterGender.MALE]: { [Locale.ZH_CN]: '男性', [Locale.EN_US]: 'Male' },
  [CharacterGender.FEMALE]: { [Locale.ZH_CN]: '女性', [Locale.EN_US]: 'Female' },
  [CharacterGender.OTHER]: { [Locale.ZH_CN]: '其他', [Locale.EN_US]: 'Other' },
};

/**
 * 年龄区间
 */
export interface AgeRange {
  min?: number;  // 最小年龄
  max?: number;  // 最大年龄
}

export interface IProjectAsset extends BaseModelAttributes {
  projectId?: string;           // 所属项目ID
  episodeId?: string;          // 所属剧集ID（可空）
  userId?: string;             // 所属用户ID（系统级主体时为 undefined/null）
  sourceAssetId?: string;      // 源资源ID（收藏时记录）
  assetType: AssetType;        // 资源类型
  name: string;                // 名称
  prompt?: string;             // 绘图提示词（用于AI生成图片）

  // 图片字段（各角度概念图）
  conceptImageUrl?: string;    // 概念图
  frontViewUrl?: string;       // 正面视图
  sideViewUrl?: string;        // 侧面视图
  backViewUrl?: string;        // 背面视图
  detailImageUrl?: string;     // 细节图

  // 主体描述
  description?: string;        // 主体描述（角色性格/道具用途/场景氛围等）
  artStyle?: string;           // 艺术风格（用于生成图片）

  // 场景专属字段
  location?: string;           // 场景地点标识，仅 SCENE 类型有效

  // 角色专属字段
  role?: CharacterRole;        // 角色类型（仅角色类型有效）
  voiceReference?: string;     // 参考音色描述或音频URL
  voiceModelId?: string;       // 关联的语音模型ID
  age?: number;                // 年龄（仅角色类型有效）
  gender?: CharacterGender;    // 性别（仅角色类型有效）
  country?: string;            // 国家/地区（仅角色类型有效）
  assetId?: string;            // 资源标识符

  // 生成状态
  generationStatus?: AssetGenerationStatus;  // 生成状态
  generationModelId?: string;  // 生成使用的模型ID
  generationTaskId?: string;   // 关联的生成任务ID
  generationError?: string;    // 生成错误信息

  // 排序和状态
  sortOrder?: number;          // 排序

  // 关联
  project?: {
    id: string;
    name: string;
  };
}

export interface IProjectAssetWithFavorite extends IProjectAsset {
  isFavorited: boolean;        // 是否已收藏
}

export interface CreateProjectAssetRequest {
  projectId?: string;
  episodeId?: string;
  sourceAssetId?: string;
  assetType: AssetType;
  name: string;
  prompt?: string;
  conceptImageUrl?: string;
  frontViewUrl?: string;
  sideViewUrl?: string;
  backViewUrl?: string;
  detailImageUrl?: string;
  description?: string;
  artStyle?: string;
  location?: string;           // 场景地点标识，仅 SCENE 类型有效
  role?: CharacterRole;
  voiceReference?: string;
  voiceModelId?: string;
  age?: number;
  gender?: CharacterGender;
  country?: string;
  assetId?: string;
  sortOrder?: number;
}

export interface UpdateProjectAssetRequest {
  name?: string;
  prompt?: string;
  conceptImageUrl?: string;
  frontViewUrl?: string;
  sideViewUrl?: string;
  backViewUrl?: string;
  detailImageUrl?: string;
  description?: string;
  artStyle?: string;
  location?: string;           // 场景地点标识，仅 SCENE 类型有效
  role?: CharacterRole;
  voiceReference?: string;
  voiceModelId?: string;
  age?: number;
  gender?: CharacterGender;
  country?: string;
  assetId?: string;
  sortOrder?: number;
}

export interface ProjectAssetQuery {
  search?: string;
  projectId?: string;
  episodeId?: string;
  userId?: string;
  assetType?: AssetType;
  role?: CharacterRole;
  gender?: CharacterGender;
  country?: string;
  ageMin?: number;             // 年龄最小值
  ageMax?: number;             // 年龄最大值
}


/**
 * 主体生成请求
 */
export interface GenerateAssetRequest {
  assetId: string;             // 主体ID
  modelId: string;             // 文生图模型ID
  userPrompt?: string;         // 用户自定义提示词
  artStyle?: string;           // 艺术风格（可选）
}

/**
 * 批量主体生成请求
 */
export interface BatchGenerateAssetsRequest {
  assetIds: string[];          // 主体ID列表
  modelId: string;             // 文生图模型ID
}

/** 系统级主体查询参数 */
export interface SystemAssetQuery {
  search?: string;
  assetType?: AssetType;
  gender?: CharacterGender;
  country?: string;
  ageMin?: number;
  ageMax?: number;
}

/** 创建系统级主体请求 */
export interface CreateSystemAssetRequest {
  assetType: AssetType;           // 必填
  name: string;                   // 必填
  prompt?: string;
  description?: string;
  conceptImageUrl?: string;
  frontViewUrl?: string;
  sideViewUrl?: string;
  backViewUrl?: string;
  detailImageUrl?: string;
  artStyle?: string;
  role?: CharacterRole;
  location?: string;
  age?: number;
  gender?: CharacterGender;
  country?: string;
  assetId?: string;
  sortOrder?: number;
}

/** 更新系统级主体请求 */
export interface UpdateSystemAssetRequest {
  name?: string;
  prompt?: string;
  description?: string;
  conceptImageUrl?: string;
  frontViewUrl?: string;
  sideViewUrl?: string;
  backViewUrl?: string;
  detailImageUrl?: string;
  artStyle?: string;
  role?: CharacterRole;
  location?: string;
  age?: number;
  gender?: CharacterGender;
  country?: string;
  assetId?: string;
  sortOrder?: number;
}
